import * as React from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  ChatStreamCallAnsweredElsewhereEvent,
  ChatStreamCallIncomingEvent,
  ChatStreamCallSignalEvent,
  ChatStreamCallUpdatedEvent,
  ChatStreamConversationEvent,
  ChatStreamMessageEvent,
  ChatStreamMessageRedactedEvent,
  ChatStreamMessageUpdatedEvent,
  ChatStreamPresenceEvent,
  ChatStreamReadEvent,
  ChatStreamReadyEvent,
  ChatStreamTypingEvent,
  type CallKind,
  type ChatCall,
  type ChatConversationSummary,
} from '@bits/shared/zod';
import type { z } from 'zod';
import { api } from '@/lib/api';
import {
  appendMessage,
  applyCallToList,
  applyMessageToList,
  applyReadToList,
  applyRedactionToList,
  applyUpdatedToList,
  hasConversation,
  redactInPage,
  removeConversation,
  replaceInPage,
  totalUnread,
  upsertConversation,
  type MessagePage,
} from '@/lib/chat-model';

const BASE = (import.meta.env.VITE_API_BASE ?? '') as string;
const RECONNECT_AFTER_REFUSAL_MS = 10_000;
const TYPING_TTL_MS = 4_000;

export const chatKeys = {
  conversations: ['chat', 'conversations'] as const,
  conversation: (id: string) => ['chat', 'conversation', id] as const,
  messages: (id: string) => ['chat', 'messages', id] as const,
};

type TypingMap = Readonly<Record<string, ReadonlySet<string>>>;

export type CallPhase = 'incoming' | 'outgoing' | 'joined' | 'ended';

export interface CallState {
  call: ChatCall;
  phase: CallPhase;
  from: { personId: string; name: string } | null;
}

export type SignalListener = (event: ChatStreamCallSignalEvent) => void;

interface ChatContextValue {
  meId: string | null;
  isAdmin: boolean;
  enabled: boolean;
  connected: boolean;
  online: ReadonlySet<string>;
  typing: TypingMap;
  conversations: ChatConversationSummary[] | undefined;
  conversationsLoading: boolean;
  unreadTotal: number;
  call: CallState | null;
  startCall(conversationId: string, kind: CallKind): Promise<void>;
  joinCall(callId: string): Promise<void>;
  answerCall(): Promise<void>;
  declineCall(): Promise<void>;
  hangUp(): Promise<void>;
  dismissCall(): void;
  onSignal(listener: SignalListener): () => void;
}

const ChatContext = React.createContext<ChatContextValue | null>(null);

export function useChat(): ChatContextValue {
  const value = React.useContext(ChatContext);
  if (!value) throw new Error('useChat must be used inside ChatProvider');
  return value;
}

function setList(
  queryClient: QueryClient,
  update: (list: ChatConversationSummary[]) => ChatConversationSummary[],
): void {
  queryClient.setQueryData<ChatConversationSummary[]>(chatKeys.conversations, (list) =>
    list ? update(list) : list,
  );
}

function setPage(
  queryClient: QueryClient,
  conversationId: string,
  update: (page: MessagePage) => MessagePage,
): void {
  queryClient.setQueryData<MessagePage>(chatKeys.messages(conversationId), (page) =>
    page ? update(page) : page,
  );
}

function listen<T extends z.ZodTypeAny>(
  source: EventSource,
  name: string,
  schema: T,
  handler: (data: z.infer<T>) => void,
): void {
  source.addEventListener(name, (event) => {
    let raw: unknown;
    try {
      raw = JSON.parse((event as MessageEvent<string>).data);
    } catch {
      return;
    }
    const parsed = schema.safeParse(raw);
    if (parsed.success) handler(parsed.data);
  });
}

// Mounted once at the root so the stream, presence and unread counts stay
// live on every page. Idle until the session is fully signed in (password
// rotated, 2FA done), because the stream answers 403 before that.
export function ChatProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const me = meQuery.data;
  const meId = me?.person.id ?? null;
  const isAdmin = me?.person.roles.includes('Admin') ?? false;
  const enabled =
    me !== undefined && !me.mustChangePassword && (!me.requires2fa || me.twoFactorVerified);

  const conversationsQuery = useQuery({
    queryKey: chatKeys.conversations,
    queryFn: () => api.chatConversations(),
    enabled,
    retry: false,
  });

  const [connected, setConnected] = React.useState(false);
  const [online, setOnline] = React.useState<ReadonlySet<string>>(() => new Set());
  const [typing, setTyping] = React.useState<TypingMap>({});
  const typingTimers = React.useRef(new Map<string, number>());
  const [call, setCall] = React.useState<CallState | null>(null);
  const callRef = React.useRef<CallState | null>(null);
  callRef.current = call;
  const signalListeners = React.useRef(new Set<SignalListener>());

  const clearTyping = React.useCallback((conversationId: string, personId: string) => {
    const key = `${conversationId}:${personId}`;
    const timer = typingTimers.current.get(key);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      typingTimers.current.delete(key);
    }
    setTyping((current) => {
      const people = current[conversationId];
      if (!people || !people.has(personId)) return current;
      const next = new Set(people);
      next.delete(personId);
      const out = { ...current };
      if (next.size === 0) delete out[conversationId];
      else out[conversationId] = next;
      return out;
    });
  }, []);

  const noteTyping = React.useCallback(
    (conversationId: string, personId: string) => {
      const key = `${conversationId}:${personId}`;
      const existing = typingTimers.current.get(key);
      if (existing !== undefined) window.clearTimeout(existing);
      typingTimers.current.set(
        key,
        window.setTimeout(() => clearTyping(conversationId, personId), TYPING_TTL_MS),
      );
      setTyping((current) => {
        const people = current[conversationId];
        if (people?.has(personId)) return current;
        return { ...current, [conversationId]: new Set([...(people ?? []), personId]) };
      });
    },
    [clearTyping],
  );

  React.useEffect(() => {
    if (!enabled || !meId) {
      setConnected(false);
      setOnline(new Set());
      setCall(null);
      return;
    }
    let source: EventSource | null = null;
    let retry: number | undefined;
    let disposed = false;

    const connect = (): void => {
      if (disposed) return;
      source = new EventSource(`${BASE}/api/chat/stream`, { withCredentials: true });

      listen(source, 'ready', ChatStreamReadyEvent, (data) => {
        setOnline(new Set(data.online));
        setConnected(true);
        // Anything that is not a message is not replayed, so a (re)connect
        // refreshes the list and every open thread from the server.
        void queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
        void queryClient.invalidateQueries({ queryKey: ['chat', 'messages'] });
        // A call.updated missed while the stream was down would leave a stale
        // set of tiles; the current call is re-read once per (re)connect.
        const live = callRef.current;
        if (live && (live.phase === 'joined' || live.phase === 'outgoing')) {
          void api
            .chatGetCall(live.call.id)
            .then((fresh) => {
              setCall((c) =>
                c && c.call.id === fresh.id
                  ? { ...c, call: fresh, phase: fresh.status === 'ended' ? 'ended' : c.phase }
                  : c,
              );
            })
            .catch(() => undefined);
        }
      });

      listen(source, 'message', ChatStreamMessageEvent, ({ message }) => {
        const pageKey = chatKeys.messages(message.conversationId);
        if (queryClient.getQueryData<MessagePage>(pageKey)) {
          setPage(queryClient, message.conversationId, (page) => appendMessage(page, message));
        } else if (queryClient.getQueryState(pageKey)?.fetchStatus === 'fetching') {
          // The thread's first page is still loading and was read before this
          // message committed; refetch so it is not lost until a reload.
          void queryClient.invalidateQueries({ queryKey: pageKey });
        }
        const list = queryClient.getQueryData<ChatConversationSummary[]>(chatKeys.conversations);
        if (list && !hasConversation(list, message.conversationId)) {
          void queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
        } else {
          setList(queryClient, (current) => applyMessageToList(current, message, meId));
        }
        if (message.authorId) clearTyping(message.conversationId, message.authorId);
      });

      listen(source, 'message.redacted', ChatStreamMessageRedactedEvent, (data) => {
        setPage(queryClient, data.conversationId, (page) => redactInPage(page, data.messageId));
        setList(queryClient, (current) =>
          applyRedactionToList(current, data.conversationId, data.messageId),
        );
      });

      listen(source, 'message.updated', ChatStreamMessageUpdatedEvent, ({ message }) => {
        setPage(queryClient, message.conversationId, (page) => replaceInPage(page, message));
        setList(queryClient, (current) => applyUpdatedToList(current, message));
      });

      listen(source, 'conversation', ChatStreamConversationEvent, (data) => {
        if (data.conversation === null) {
          setList(queryClient, (current) => removeConversation(current, data.conversationId));
          queryClient.removeQueries({ queryKey: chatKeys.conversation(data.conversationId) });
          queryClient.removeQueries({ queryKey: chatKeys.messages(data.conversationId) });
          return;
        }
        const conversation = data.conversation;
        setList(queryClient, (current) => upsertConversation(current, conversation));
        queryClient.setQueryData(chatKeys.conversation(conversation.id), conversation);
      });

      listen(source, 'read', ChatStreamReadEvent, (data) => {
        setList(queryClient, (current) => applyReadToList(current, data, meId));
      });

      listen(source, 'typing', ChatStreamTypingEvent, (data) => {
        if (data.personId !== meId) noteTyping(data.conversationId, data.personId);
      });

      listen(source, 'presence', ChatStreamPresenceEvent, (data) => {
        setOnline((current) => {
          if (current.has(data.personId) === data.online) return current;
          const next = new Set(current);
          if (data.online) next.add(data.personId);
          else next.delete(data.personId);
          return next;
        });
      });

      // One call at a time per tab: a second incoming call keeps ringing on the
      // server and is missed on its timeout if nobody picks it up elsewhere.
      listen(source, 'call.incoming', ChatStreamCallIncomingEvent, (data) => {
        setList(queryClient, (current) => applyCallToList(current, data.call));
        setCall((current) =>
          current && current.phase !== 'ended'
            ? current
            : { call: data.call, phase: 'incoming', from: data.from },
        );
      });

      listen(source, 'call.updated', ChatStreamCallUpdatedEvent, ({ call: updated }) => {
        setList(queryClient, (current) => applyCallToList(current, updated));
        setCall((current) => {
          if (!current || current.call.id !== updated.id) return current;
          if (updated.status === 'ended') return { ...current, call: updated, phase: 'ended' };
          const me = updated.participants.find((p) => p.personId === meId);
          // Still showing the ring here, but the server says this person
          // declined, missed or answered: another tab or the timeout acted.
          if (current.phase === 'incoming' && me && me.status !== 'invited') return null;
          return { ...current, call: updated };
        });
      });

      listen(source, 'call.answered_elsewhere', ChatStreamCallAnsweredElsewhereEvent, (data) => {
        setCall((current) =>
          current && current.call.id === data.callId && current.phase === 'incoming'
            ? null
            : current,
        );
      });

      listen(source, 'call.signal', ChatStreamCallSignalEvent, (data) => {
        for (const listener of signalListeners.current) listener(data);
      });

      source.onerror = () => {
        setConnected(false);
        // A dropped connection reconnects by itself with Last-Event-ID. A
        // refusal (session gone, 2FA pending) closes the source for good, so
        // try again later once `me` has been re-read.
        if (source?.readyState === EventSource.CLOSED) {
          source = null;
          void queryClient.invalidateQueries({ queryKey: ['me'] });
          retry = window.setTimeout(connect, RECONNECT_AFTER_REFUSAL_MS);
        }
      };
    };

    connect();
    const timers = typingTimers.current;
    return () => {
      disposed = true;
      if (retry !== undefined) window.clearTimeout(retry);
      source?.close();
      for (const timer of timers.values()) window.clearTimeout(timer);
      timers.clear();
      setTyping({});
    };
  }, [enabled, meId, queryClient, clearTyping, noteTyping]);

  const startCall = React.useCallback(async (conversationId: string, kind: CallKind) => {
    const created = await api.chatStartCall({ conversationId, kind });
    setCall({ call: created, phase: 'outgoing', from: null });
  }, []);

  const joinCall = React.useCallback(async (callId: string) => {
    const joined = await api.chatAnswerCall(callId);
    setCall({ call: joined, phase: 'joined', from: null });
  }, []);

  const answerCall = React.useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    setCall({ ...current, phase: 'joined' });
    try {
      const joined = await api.chatAnswerCall(current.call.id);
      setCall((c) => (c && c.call.id === joined.id ? { ...c, call: joined } : c));
    } catch (err) {
      setCall(null);
      throw err;
    }
  }, []);

  const declineCall = React.useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    setCall(null);
    await api.chatDeclineCall(current.call.id).catch(() => undefined);
  }, []);

  const hangUp = React.useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    try {
      const after = await api.chatLeaveCall(current.call.id);
      setCall(after.status === 'ended' ? { ...current, call: after, phase: 'ended' } : null);
    } catch {
      setCall(null);
    }
  }, []);

  const dismissCall = React.useCallback(() => setCall(null), []);

  const onSignal = React.useCallback((listener: SignalListener) => {
    signalListeners.current.add(listener);
    return () => {
      signalListeners.current.delete(listener);
    };
  }, []);

  const conversations = conversationsQuery.data;
  const value = React.useMemo<ChatContextValue>(
    () => ({
      meId,
      isAdmin,
      enabled,
      connected,
      online,
      typing,
      conversations,
      conversationsLoading: enabled && conversationsQuery.isPending,
      unreadTotal: conversations ? totalUnread(conversations) : 0,
      call,
      startCall,
      joinCall,
      answerCall,
      declineCall,
      hangUp,
      dismissCall,
      onSignal,
    }),
    [
      meId,
      isAdmin,
      enabled,
      connected,
      online,
      typing,
      conversations,
      conversationsQuery.isPending,
      call,
      startCall,
      joinCall,
      answerCall,
      declineCall,
      hangUp,
      dismissCall,
      onSignal,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
